import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('preferred-language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('preferred-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const changeLanguage = (lang) => {
    setLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Translation utility
export const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      conditions: 'Conditions',
      about: 'About',
      blog: 'Blog',
      resources: 'Resources',
      contact: 'Contact',
      consultSpecialist: 'Consult Specialist'
    },
    // Home Page
    home: {
      heroTitle: 'Shoulder Pain?',
      heroSubtitle: 'Get Expert Care',
      heroDescription: 'Comprehensive shoulder care information from an experienced orthopaedic specialist. Understand your condition and explore the best treatment options.',
      commonProblems: 'Common Shoulder Problems',
      commonProblemsDesc: 'Expert information on shoulder conditions, symptoms, and treatment options',
      whenToSee: 'When to See a Shoulder Specialist',
      treatmentPhilosophy: 'Treatment Philosophy',
      learnMore: 'Learn More'
    },
    // Common
    common: {
      bookAppointment: 'Book Appointment',
      readMore: 'Read More',
      downloadPDF: 'Download Free PDF',
      viewAll: 'View All',
      submit: 'Submit',
      reset: 'Reset',
      loading: 'Loading...',
      error: 'Error',
      success: 'Success'
    },
    // Conditions
    conditions: {
      shoulderPain: 'Shoulder Pain',
      rotatorCuff: 'Rotator Cuff Tear',
      frozenShoulder: 'Frozen Shoulder',
      dislocation: 'Shoulder Dislocation',
      arthritis: 'Shoulder Arthritis',
      fracture: 'Shoulder Fracture',
      impingement: 'Shoulder Impingement',
      bicepsTendon: 'Biceps Tendon Injury',
      replacement: 'Shoulder Replacement',
      arthroscopy: 'Arthroscopic Surgery'
    },
    // Footer
    footer: {
      quickLinks: 'Quick Links',
      conditions: 'Conditions',
      getExpertCare: 'Get Expert Care',
      visitMainWebsite: 'Visit Main Website',
      educational: 'Educational resource for shoulder care. For appointments, visit',
      disclaimer: 'This website provides general medical information and is not a substitute for professional medical advice.'
    }
  },
  hi: {
    // Navigation
    nav: {
      home: 'होम',
      conditions: 'स्थितियाँ',
      about: 'हमारे बारे में',
      blog: 'ब्लॉग',
      resources: 'संसाधन',
      contact: 'संपर्क करें',
      consultSpecialist: 'विशेषज्ञ से परामर्श करें'
    },
    // Home Page
    home: {
      heroTitle: 'कंधे में दर्द?',
      heroSubtitle: 'विशेषज्ञ देखभाल प्राप्त करें',
      heroDescription: 'अनुभवी आर्थोपेडिक विशेषज्ञ से व्यापक कंधे की देखभाल की जानकारी। अपनी स्थिति को समझें और सर्वोत्तम उपचार विकल्पों का पता लगाएं।',
      commonProblems: 'सामान्य कंधे की समस्याएं',
      commonProblemsDesc: 'कंधे की स्थितियों, लक्षणों और उपचार विकल्पों पर विशेषज्ञ जानकारी',
      whenToSee: 'कंधे के विशेषज्ञ को कब दिखाना चाहिए',
      treatmentPhilosophy: 'उपचार दर्शन',
      learnMore: 'और जानें'
    },
    // Common
    common: {
      bookAppointment: 'अपॉइंटमेंट बुक करें',
      readMore: 'और पढ़ें',
      downloadPDF: 'मुफ्त पीडीएफ डाउनलोड करें',
      viewAll: 'सभी देखें',
      submit: 'जमा करें',
      reset: 'रीसेट करें',
      loading: 'लोड हो रहा है...',
      error: 'त्रुटि',
      success: 'सफलता'
    },
    // Conditions
    conditions: {
      shoulderPain: 'कंधे का दर्द',
      rotatorCuff: 'रोटेटर कफ का फटना',
      frozenShoulder: 'फ्रोजन शोल्डर',
      dislocation: 'कंधे की अव्यवस्था',
      arthritis: 'कंधे का गठिया',
      fracture: 'कंधे की फ्रैक्चर',
      impingement: 'शोल्डर इंपिंजमेंट',
      bicepsTendon: 'बाइसेप्स टेंडन की चोट',
      replacement: 'कंधे का प्रतिस्थापन',
      arthroscopy: 'आर्थ्रोस्कोपिक सर्जरी'
    },
    // Footer
    footer: {
      quickLinks: 'त्वरित लिंक',
      conditions: 'स्थितियाँ',
      getExpertCare: 'विशेषज्ञ देखभाल प्राप्त करें',
      visitMainWebsite: 'मुख्य वेबसाइट पर जाएं',
      educational: 'कंधे की देखभाल के लिए शैक्षिक संसाधन। अपॉइंटमेंट के लिए, यहां जाएं',
      disclaimer: 'यह वेबसाइट सामान्य चिकित्सा जानकारी प्रदान करती है और यह पेशेवर चिकित्सा सलाह का विकल्प नहीं है।'
    }
  },
  te: {
    // Navigation
    nav: {
      home: 'హోమ్',
      conditions: 'పరిస్థితులు',
      about: 'మా గురించి',
      blog: 'బ్లాగ్',
      resources: 'వనరులు',
      contact: 'సంప్రదించండి',
      consultSpecialist: 'నిపుణుడిని సంప్రదించండి'
    },
    // Home Page
    home: {
      heroTitle: 'భుజం నొప్పి?',
      heroSubtitle: 'నిపుణుల సంరక్షణ పొందండి',
      heroDescription: 'అనుభవజ్ఞులైన ఆర్థోపెడిక్ నిపుణుల నుండి సమగ్ర భుజం సంరక్షణ సమాచారం. మీ పరిస్థితిని అర్థం చేసుకోండి మరియు ఉత్తమ చికిత్స ఎంపికలను అన్వేషించండి।',
      commonProblems: 'సాధారణ భుజం సమస్యలు',
      commonProblemsDesc: 'భుజం పరిస్థితులు, లక్షణాలు మరియు చికిత్స ఎంపికలపై నిపుణుల సమాచారం',
      whenToSee: 'భుజం నిపుణుడిని ఎప్పుడు కలవాలి',
      treatmentPhilosophy: 'చికిత్స తత్వశాస్త్రం',
      learnMore: 'మరింత తెలుసుకోండి'
    },
    // Common
    common: {
      bookAppointment: 'అపాయింట్మెంట్ బుక్ చేయండి',
      readMore: 'మరింత చదవండి',
      downloadPDF: 'ఉచిత PDF డౌన్లోడ్ చేయండి',
      viewAll: 'అన్నీ చూడండి',
      submit: 'సమర్పించండి',
      reset: 'రీసెట్ చేయండి',
      loading: 'లోడ్ అవుతోంది...',
      error: 'లోపం',
      success: 'విజయం'
    },
    // Conditions
    conditions: {
      shoulderPain: 'భుజం నొప్పి',
      rotatorCuff: 'రొటేటర్ కఫ్ టియర్',
      frozenShoulder: 'ఫ్రోజెన్ షోల్డర్',
      dislocation: 'భుజం తొలగుట',
      arthritis: 'భుజం ఆర్థరైటిస్',
      fracture: 'భుజం ఫ్రాక్చర్',
      impingement: 'షోల్డర్ ఇంపింజ్మెంట్',
      bicepsTendon: 'బైసెప్స్ టెండన్ గాయం',
      replacement: 'భుజం మార్పిడి',
      arthroscopy: 'ఆర్థ్రోస్కోపిక్ శస్త్రచికిత్స'
    },
    // Footer
    footer: {
      quickLinks: 'శీఘ్ర లింక్లు',
      conditions: 'పరిస్థితులు',
      getExpertCare: 'నిపుణుల సంరక్షణ పొందండి',
      visitMainWebsite: 'ప్రధాన వెబ్సైట్ను సందర్శించండి',
      educational: 'భుజం సంరక్షణ కోసం విద్యా వనరు. అపాయింట్మెంట్ల కోసం, సందర్శించండి',
      disclaimer: 'ఈ వెబ్సైట్ సాధారణ వైద్య సమాచారాన్ని అందిస్తుంది మరియు ఇది వృత్తిపరమైన వైద్య సలహాకు ప్రత్యామ్నాయం కాదు।'
    }
  }
};

export const useTranslation = () => {
  const { language } = useLanguage();
  
  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
    }
    
    return value || key;
  };
  
  return { t, language };
};